import axios, { AxiosInstance } from 'axios';

export interface ServicePublicContent {
    id: number;
    title: string;
    description: string;
    file: string;
    created_at: string;
    updated_at: string;
}

export interface CreateServicePublicData {
    title: string;
    description: string;
    file: File;
}

export interface UpdateServicePublicData {
    id: number;
    title?: string;
    description?: string;
    file?: File;
}

class ServicePublicContentService {
    private axiosInstance: AxiosInstance;

    constructor() {
        this.axiosInstance = axios.create({
            baseURL: process.env.REACT_APP_API_URL,
            timeout: 10000,
        });

        this.axiosInstance.interceptors.request.use(
            (config) => {
                const storageType = localStorage.getItem('storageType');
                const storage = storageType === 'local' ? localStorage : sessionStorage;
                const token = storage.getItem('token');
                if (token && config.headers) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                return config;
            },
            (error) => Promise.reject(error)
        );
    }

    async createServicePublicContent(data: CreateServicePublicData): Promise<ServicePublicContent | undefined> {
        try {
            const formData = new FormData();
            formData.append('title', data.title);
            formData.append('description', data.description);
            formData.append('file', data.file);
    
            const response = await this.axiosInstance.post<{
                message: string;
                data: ServicePublicContent;
            }>('/admin/service-public-content', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data.data;
        } catch (error) {
            this.handleError(error, 'Failed to create service public content');
            return undefined;
        }
    }

    async getServicePublicContent(): Promise<ServicePublicContent[]> {
        try {
            const response = await this.axiosInstance.get<{
                data: ServicePublicContent[];
            }>('/service-public-content');
            return response.data.data;
        } catch (error) {
            this.handleError(error, 'Failed to retrieve service public content');
            return [];
        }
    }

    async getServicePublicContentById(id: number): Promise<ServicePublicContent | undefined> {
        try {
            const response = await this.axiosInstance.get<{
                data: ServicePublicContent;
            }>(`/service-public-content/${id}`);
            return response.data.data;
        } catch (error) {
            this.handleError(error, 'Failed to retrieve service public content');
            return undefined;
        }
    }

    async updateServicePublicContent(data: UpdateServicePublicData): Promise<ServicePublicContent | undefined> {
        try {
            const formData = new FormData();
            
            if (data.title) formData.append('title', data.title);
            if (data.description) formData.append('description', data.description);
            if (data.file) formData.append('file', data.file);
    
            const response = await this.axiosInstance.put<{
                message: string;
                data: ServicePublicContent;
            }>(`/admin/service-public-content/${data.id}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            
            return response.data.data;
        } catch (error) {
            this.handleError(error, 'Failed to update service public content');
            return undefined;
        }
    }

    async deleteServicePublicContent(id: number): Promise<void> {
        try {
            await this.axiosInstance.delete(`/admin/service-public-content/${id}`);
        } catch (error) {
            this.handleError(error, 'Failed to delete service public content');
        }
    }

    async downloadPDFFile(id: number): Promise<void> {
        try {
            const response = await this.axiosInstance.get(`/service-public-content/${id}/download`, {
                responseType: 'blob'
            });

            // Create a link element to trigger the download
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            
            // Try to get filename from Content-Disposition header
            const filename = response.headers['content-disposition']
                ?.split('filename=')[1] || `service-public-content-${id}.pdf`;
            
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            
            // Clean up
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            this.handleError(error, 'Failed to download PDF file');
        }
    }

    async viewPDFFile(id: number): Promise<string | undefined> {
        try {
            const response = await this.axiosInstance.get(`/service-public-content/${id}/view`, {
                responseType: 'blob'
            });

            // Create a blob URL to view the PDF
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            return url;
        } catch (error) {
            this.handleError(error, 'Failed to view PDF file');
            return undefined;
        }
    }

    private handleError(error: unknown, message: string) {
        if (axios.isAxiosError(error)) {
            console.error(`${message}:`, error.response?.data);
            throw error.response?.data || { error: message };
        }
        console.error(message, error);
        throw { error: message };
    }
}

export const servicePublicContentService = new ServicePublicContentService();